import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Logo from './Logo';

// Hero Section
const HeroSection = styled.section`
  height: 100vh;
  background-color: var(--black);
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
`;

const HeroPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: radial-gradient(var(--primary-light) 1px, transparent 1px);
  background-size: 30px 30px;
  opacity: 0.3;
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  color: var(--white);
  position: relative;
  z-index: 2;
`;

const HeroLogo = styled.div`
  margin-bottom: 2rem;
  img {
    height: 45px;
    width: auto;
  }
  
  @media (max-width: 768px) {
    img {
      height: 60px;
    }
  }
`;

const HeroTitle = styled(motion.h1)`
  font-size: 4rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.5rem;
  margin-bottom: 2.5rem;
  max-width: 600px;
  line-height: 1.6;
  color: var(--gray-300);
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  
  @media (max-width: 576px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const PrimaryButton = styled(motion.button)`
  background-color: var(--primary);
  color: var(--black);
  border: none;
  border-radius: 4px;
  padding: 1rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: var(--primary-dark);
  }
`;

const SecondaryButton = styled(motion.button)`
  background-color: transparent;
  color: var(--white);
  border: 2px solid var(--white);
  border-radius: 4px;
  padding: 1rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

// About Section
const AboutSection = styled.section`
  padding: 8rem 0;
  background-color: var(--gray-100);
`;

const AboutContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 2rem;
  text-align: center;
`;

const AboutHeading = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 2rem;
  color: var(--black);
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const AboutText = styled.p`
  font-size: 1.2rem;
  line-height: 1.8;
  color: var(--gray-800);
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

// Services Section
const ServicesSection = styled.section`
  padding: 8rem 0;
  background-color: var(--white);
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.2rem;
  text-align: center;
  color: var(--gray-600);
  max-width: 700px;
  margin: 0 auto 4rem;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled(motion.div)`
  background-color: var(--white);
  border-radius: 8px;
  padding: 2.5rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  border: 1px solid var(--gray-200);
  
  &:hover {
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
    transform: translateY(-5px);
    border-color: var(--primary);
  }
`;

const ServiceIcon = styled.div`
  width: 60px;
  height: 60px;
  background-color: var(--primary-light);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  color: var(--primary);
  font-size: 1.5rem;
`;

const ServiceTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const ServiceDescription = styled.p`
  color: var(--gray-700);
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const ServiceLink = styled(Link)`
  color: var(--primary);
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    gap: 0.75rem;
  }
`;

// How It Works Section
const ProcessSection = styled.section`
  padding: 8rem 0;
  background-color: var(--gray-100);
`;

const ProcessSteps = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const ProcessStep = styled(motion.div)`
  text-align: center;
  padding: 2rem;
`;

const StepNumber = styled.div`
  width: 50px;
  height: 50px;
  background-color: var(--primary);
  color: var(--white);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 auto 1.5rem;
`;

const StepTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const StepDescription = styled.p`
  color: var(--gray-700);
  line-height: 1.6;
`;

// CTA Section
const CTASection = styled.section`
  padding: 6rem 0;
  background-color: var(--black);
  color: var(--white);
  text-align: center;
`;

const CTAContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const CTATitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const CTADescription = styled.p`
  font-size: 1.2rem;
  margin-bottom: 2.5rem;
  color: var(--gray-300);
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const HomePage = () => {
  return (
    <>
      <HeroSection >
        <HeroPattern />
        <HeroContent style={{ width: '100%' }}>
          <HeroLogo>
            <Logo color="white" height="180px" fullName={true} />
           
          </HeroLogo>
          <HeroTitle
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Where <span style={{ color: '#30e8be' }}>IT</span> all <span style={{ color: '#30e8be' }}>Begins</span>
          </HeroTitle>
          <HeroSubtitle
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            We help businesses design, implement, and optimize scalable architecture for modern, intelligent systems.
          </HeroSubtitle>
          <ButtonGroup>
            <Link to="/contact">
              <PrimaryButton
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                Get Started
              </PrimaryButton>
            </Link>
            <Link to="/services">
              <SecondaryButton
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
              >
                Our Services
              </SecondaryButton>
            </Link>
          </ButtonGroup>
        </HeroContent>
      </HeroSection>
      
      <AboutSection>
        <AboutContainer>
          <AboutHeading>About Platform0</AboutHeading>
          <AboutText>
          Platform0 provides expert architectural consulting for companies aiming to build modern digital systems. We help you plan, design, and optimize your system architecture, covering IT infrastructure, cloud solutions, digital transformation, and AI integration strategies. We’re your strategic partner dedicated to driving your technology success.          </AboutText>
        </AboutContainer>
      </AboutSection>
      
      <ServicesSection>
        <SectionTitle>What We Do</SectionTitle>
        <SectionSubtitle>
          We provide comprehensive solutions to help your business leverage cutting-edge technology and architecture.
        </SectionSubtitle>
        
        <ServicesGrid>
          <ServiceCard
            whileHover={{ y: -10 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <ServiceIcon>A</ServiceIcon>
            <ServiceTitle>Architecture as a Service</ServiceTitle>
            <ServiceDescription>
              Our team of expert architects designs scalable, resilient, and efficient systems tailored to your business needs. We handle the complexity so you can focus on your core business.
            </ServiceDescription>
            <ServiceLink to="/services#architecture">
              Learn more <span>&rarr;</span>
            </ServiceLink>
          </ServiceCard>
          
          <ServiceCard
            whileHover={{ y: -10 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <ServiceIcon>AI</ServiceIcon>
            <ServiceTitle>AI Consultancy</ServiceTitle>
            <ServiceDescription>
              Leverage the power of artificial intelligence to transform your business. We help you identify opportunities, implement AI solutions, and maximize ROI from your AI investments.
            </ServiceDescription>
            <ServiceLink to="/services#ai-consultancy">
              Learn more <span>&rarr;</span>
            </ServiceLink>
          </ServiceCard>
          
          <ServiceCard
            whileHover={{ y: -10 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <ServiceIcon>C</ServiceIcon>
            <ServiceTitle>Cloud Solutions</ServiceTitle>
            <ServiceDescription>
              Migrate to the cloud or optimize your existing cloud infrastructure. We design and implement cloud-native architectures that are secure, cost-effective, and scalable.
            </ServiceDescription>
            <ServiceLink to="/services#cloud-solutions">
              Learn more <span>&rarr;</span>
            </ServiceLink>
          </ServiceCard>
          
          <ServiceCard
            whileHover={{ y: -10 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <ServiceIcon>D</ServiceIcon>
            <ServiceTitle>Digital Transformation</ServiceTitle>
            <ServiceDescription>
              Modernize your business with end-to-end digital transformation. We help you reimagine your processes, systems, and customer experiences for the digital age.
            </ServiceDescription>
            <ServiceLink to="/services#digital-transformation">
              Learn more <span>&rarr;</span>
            </ServiceLink>
          </ServiceCard>
        </ServicesGrid>
      </ServicesSection>
      
      <ProcessSection>
        <SectionTitle>How It Works</SectionTitle>
        <SectionSubtitle>
          Our streamlined process ensures we deliver exceptional results for your business.
        </SectionSubtitle>
        
        <ProcessSteps>
          <ProcessStep
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <StepNumber>1</StepNumber>
            <StepTitle>Discovery Workshops</StepTitle>
            <StepDescription>
              We start by understanding your business goals, challenges, and existing architecture to identify opportunities for improvement.
            </StepDescription>
          </ProcessStep>
          
          <ProcessStep
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <StepNumber>2</StepNumber>
            <StepTitle>Design</StepTitle>
            <StepDescription>
              Our experts design a tailored solution that addresses your specific needs, leveraging best practices and cutting-edge technologies.
            </StepDescription>
          </ProcessStep>
          
          <ProcessStep
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <StepNumber>3</StepNumber>
            <StepTitle>Strategy Roadmap</StepTitle>
            <StepDescription>
              Prioritize next steps and options to help you make informed decisions about your technology investments and implementation path.
            </StepDescription>
          </ProcessStep>
        </ProcessSteps>
      </ProcessSection>
      
      <CTASection>
        <CTAContent>
          <CTATitle>Ready to transform your business?</CTATitle>
          <CTADescription>
            Let's discuss how Platform0 can help you leverage architecture and AI to achieve your business goals.
          </CTADescription>
          <Link to="/contact">
            <PrimaryButton
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Schedule a Consultation
            </PrimaryButton>
          </Link>
        </CTAContent>
      </CTASection>
    </>
  );
};

export default HomePage;
